/**
 * RideXelite audible feedback — Web Audio API synthesized sounds.
 *
 * Two flavors:
 *   - "tock": soft UI click for standard CTAs (Book now, Get started, etc.)
 *   - "coin": elegant gold chime for gift / checkout / confirm actions.
 *
 * Global mute state is persisted in localStorage under `ridexelite:sound-muted`.
 * Default is unmuted; user can toggle via the header speaker button.
 */

const STORAGE_KEY = "ridexelite:sound-muted";
let audioCtx = null;

function getCtx() {
  if (audioCtx) return audioCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = new AC();
  return audioCtx;
}

export function isSoundMuted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSoundMuted(muted) {
  try {
    localStorage.setItem(STORAGE_KEY, muted ? "1" : "0");
    window.dispatchEvent(new CustomEvent("ridexelite-sound-muted", { detail: { muted } }));
  } catch {
    // ignore
  }
}

function playTock() {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  // Soft low click ~ 380Hz sweeping down to 260Hz over 40ms
  osc.frequency.setValueAtTime(380, now);
  osc.frequency.exponentialRampToValueAtTime(260, now + 0.04);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.1);
}

function playCoin() {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // Two-tone gold chime: bright triangle wave, 1046 (C6) -> 1568 (G6)
  const tones = [
    { freq: 1046.5, start: 0, dur: 0.18, gain: 0.18 },
    { freq: 1568.0, start: 0.06, dur: 0.28, gain: 0.14 },
  ];
  tones.forEach((t) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(t.freq, now + t.start);
    gain.gain.setValueAtTime(0.0001, now + t.start);
    gain.gain.exponentialRampToValueAtTime(t.gain, now + t.start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + t.start);
    osc.stop(now + t.start + t.dur + 0.02);
  });
}

export function playSound(kind) {
  if (isSoundMuted()) return;
  // Resume context on-demand (browsers suspend it until first user gesture)
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  try {
    if (kind === "coin") playCoin();
    else playTock();
  } catch {
    // ignore audio failures silently
  }
}

/**
 * Attach a global click listener that reads `data-sound="tock|coin"` from the
 * clicked element (or any ancestor) and plays the corresponding sound.
 * Returns an `unsubscribe` function.
 */
export function attachGlobalClickSounds() {
  const handler = (e) => {
    const el = e.target && e.target.closest && e.target.closest("[data-sound]");
    if (!el) return;
    // Skip disabled elements
    if (el.getAttribute("aria-disabled") === "true" || el.disabled) return;
    const kind = el.getAttribute("data-sound");
    playSound(kind === "coin" ? "coin" : "tock");
  };
  document.addEventListener("click", handler, true); // capture to fire before navigation
  return () => document.removeEventListener("click", handler, true);
}
